import { z } from 'zod/v4';
import { ApiError, api } from '@shared/api';
import {
  StockSourcesConflictError,
  type StockSourceSaveRule,
  type StockSourcesPort,
  type StockSourcesSnapshot,
} from '../stock-sources/stock-sources-editor';

const PATH = '/api/v1/shopify/stock-source-mappings';
const strongEtagSchema = z.string().regex(/^"[^"\r\n]+"$/u);
const variantSchema = z.object({
  variantId: z.string().min(1),
  shopifySku: z.string(),
  inventoryItemId: z.string().nullable(),
  identityStatus: z.enum(['valid', 'missing', 'ambiguous', 'untracked']),
});
const candidateSchema = z.object({
  variant: variantSchema,
  kind: z.enum(['override', 'shared']),
  sharedGroupId: z.string().nullable(),
  suggestedProductId: z.string().nullable(),
  suggestedSourceSku: z.string(),
});

/** Runtime-validated local mapping response; extra server fields are intentionally stripped. */
export const stockSourceMappingsSchema = z.object({
  data: z.object({
    scope: z.object({
      tenantId: z.string().min(1),
      environment: z.string().min(1),
      storeDomain: z.string().min(1),
    }),
    revision: z.number().int().nonnegative(),
    updatedAt: z.iso.datetime().nullable(),
    updatedBy: z.string().nullable(),
    canSave: z.boolean(),
    variants: z.array(variantSchema).max(10_000),
    products: z
      .array(
        z.object({
          productId: z.string().min(1),
          sourceSku: z.string(),
          label: z.string(),
          available: z.boolean(),
        })
      )
      .max(10_000),
    savedRules: z
      .array(
        z.object({
          variantId: z.string().min(1),
          sourceProductId: z.string().min(1),
          kind: z.enum(['override', 'shared']),
          sharedGroupId: z.string().nullable(),
          observedShopifySku: z.string(),
          observedSourceSku: z.string(),
        })
      )
      .max(10_000),
    candidates: z.array(candidateSchema).max(10_000),
    validation: z.object({
      valid: z.boolean(),
      issues: z.array(
        z.object({ ruleIndex: z.number().int().nonnegative().nullable(), code: z.string() })
      ),
    }),
  }),
});

const saveRuleSchema = z
  .object({
    variantId: z.string().regex(/^gid:\/\/shopify\/ProductVariant\/[0-9]+$/u),
    sourceProductId: z.string().min(1),
    kind: z.enum(['override', 'shared']),
    observedShopifySku: z.string(),
    observedSourceSku: z.string(),
  })
  .strict();
const saveBodySchema = z.object({ rules: z.array(saveRuleSchema).max(10_000) }).strict();

function toSnapshot(response: {
  data: z.infer<typeof stockSourceMappingsSchema>;
  headers: Headers;
}): StockSourcesSnapshot {
  const parsed = response.data.data;
  return {
    scope: parsed.scope,
    revision: parsed.revision,
    etag: strongEtagSchema.parse(response.headers.get('etag')),
    updatedAt: parsed.updatedAt,
    updatedBy: parsed.updatedBy,
    canSave: parsed.canSave,
    variants: parsed.variants,
    products: parsed.products,
    savedRules: parsed.savedRules.map((rule) => ({
      ...rule,
      sharedGroupId: rule.kind === 'shared' ? `source:${rule.sourceProductId}` : null,
    })),
    candidates: parsed.candidates,
    validation: parsed.validation,
  };
}

/** The only web adapter for local stock-source configuration; no Shopify mutation is exposed. */
export const stockSourcesPort: StockSourcesPort = {
  async load() {
    return toSnapshot(await api.getWithMetadata(PATH, { schema: stockSourceMappingsSchema }));
  },
  async save(input: { ifMatch: string; rules: readonly StockSourceSaveRule[] }) {
    const ifMatch = strongEtagSchema.parse(input.ifMatch);
    const body = saveBodySchema.parse({
      rules: input.rules.map(
        ({ variantId, sourceProductId, kind, observedShopifySku, observedSourceSku }) => ({
          variantId,
          sourceProductId,
          kind,
          observedShopifySku,
          observedSourceSku,
        })
      ),
    });
    try {
      return toSnapshot(
        await api.putWithMetadata(PATH, body, {
          headers: { 'If-Match': ifMatch },
          schema: stockSourceMappingsSchema,
        })
      );
    } catch (error) {
      if (error instanceof ApiError && error.status === 412) throw new StockSourcesConflictError();
      throw error;
    }
  },
};
